import { CardContent, Grid2, Stack, Typography } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import StackedIcon from '@components/Icon/StackedIcon';

import { VITAL_SIGN_TYPES } from '@constants/fhir';
import { BODY_SCAN_OBS_ORDER, FACE_SCAN_OBS_ORDER } from '@constants/scan';

import { useIsMobile } from '@hooks/useIsMobile';

import type { ObservationRecord } from '@models/observation';
import type { ScanResult } from '@models/scan';

import { formatDate, formatVitalSign } from '@utils/format';

import { ScanType } from 'generated/types';

import { FontWeight } from '../../../../theme/typography';

interface FaceBodyScanContentProps {
  data: ScanResult;
}

export default function FaceBodyScanContent({
  data: { date, observations = [], scan_type },
}: FaceBodyScanContentProps) {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();

  const scanObservationsMap = observations.reduce<
    Record<string, ObservationRecord>
  >((acc, curr) => {
    acc[curr.code] = curr;
    return acc;
  }, {});

  return (
    <CardContent>
      <Grid2 container columnSpacing={{ xs: 5, sm: 1 }} rowSpacing={2}>
        <Grid2 size={{ xs: 12 }}>
          <Typography
            color="text.secondary"
            variant={isMobile ? 'caption' : 'subtitle2'}
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse',
              mb: 1,
            }}
          >
            {formatDate({ date, formatter: 'datetime' })}
          </Typography>
        </Grid2>

        {(scan_type === ScanType.face
          ? FACE_SCAN_OBS_ORDER
          : BODY_SCAN_OBS_ORDER
        ).map((code) => {
          const { format, icon, messageId, unit } = VITAL_SIGN_TYPES[code];
          const observation =
            code in scanObservationsMap ? scanObservationsMap[code] : undefined;
          if (!observation) {
            return null;
          }

          return (
            <Grid2
              key={`${scan_type}-scan-observation-${observation.id}`}
              size={{ xs: 'auto' }}
            >
              <Stack alignItems="center" direction="row" spacing={2}>
                <StackedIcon icons={icon.map((i) => ({ size: 'xl', ...i }))} />

                <Stack>
                  <Typography color="text.secondary" variant="caption">
                    {formatMessage({ id: messageId })}
                  </Typography>
                  <Stack alignItems="baseline" direction="row" spacing={0.5}>
                    <Typography variant="h6" fontWeight={FontWeight.Light}>
                      {formatVitalSign(observation, false, format)}
                    </Typography>
                    <Typography
                      color="grey.500"
                      variant="subtitle2"
                      fontWeight={FontWeight.Light}
                    >
                      {unit}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Grid2>
          );
        })}
      </Grid2>
    </CardContent>
  );
}
